'use client'

import Image from 'next/image'
import { Group, Skeleton, Stack, Text, UnstyledButton } from '@mantine/core'
import { humanizePrice } from '@/lib/utilities'
import { getListRowIcon } from './pharmacyChainIcon'
import classes from './PharmacyQuickFilters.module.css'

// The only pricing groups with a quick-filter chip — "Other Pharmacy" spans many
// unrelated chains with no single icon/name to show, so it's only reachable via search.
export const QUICK_FILTER_PRICING_GROUPS = ['cvs', 'walgreens', 'walmart'] as const
export type QuickFilterPricingGroup = (typeof QUICK_FILTER_PRICING_GROUPS)[number]

export const LABEL_BY_PRICING_GROUP: Record<QuickFilterPricingGroup, string> = {
    cvs: 'CVS',
    walgreens: 'Walgreens',
    walmart: 'Walmart',
}

interface PharmacyQuickFiltersProps {
    /** Uniform price for each pricing group, keyed by group name; a group with no
     *  nearby location has no entry here and its chip shows "Priced after visit". */
    priceCentsByGroup: Partial<Record<QuickFilterPricingGroup, number | null>>
    /** True while the pharmacy search is still in flight — each chip shows a loading
     *  skeleton (matching MedicationPriceCard's) instead of "Priced after visit",
     *  since priceCentsByGroup is empty before the first response and isn't yet a
     *  reliable "no nearby location" signal. */
    isLoading: boolean
    selected: QuickFilterPricingGroup | null
    onSelect: (group: QuickFilterPricingGroup | null) => void
}

/**
 * Single-select brand chips for the Compare pharmacies map view — always CVS,
 * Walgreens, and Walmart, ranked by price low to high (a group with no nearby
 * location sorts last and shows "Priced after visit" instead of a price). Selecting a
 * chip shows every location in that pricing group already present in the current
 * result set; a group with no nearby location (or still loading) is not selectable,
 * so the list/map never filter down to an empty group. Clicking the already-selected
 * chip deselects it, returning to the default merged view.
 */
export function PharmacyQuickFilters({ priceCentsByGroup, isLoading, selected, onSelect }: PharmacyQuickFiltersProps) {
    const orderedGroups = [...QUICK_FILTER_PRICING_GROUPS].sort(
        (a, b) => (priceCentsByGroup[a] ?? Infinity) - (priceCentsByGroup[b] ?? Infinity),
    )

    return (
        <Group gap={8} wrap="nowrap" className={classes.row}>
            {orderedGroups.map((group) => {
                const icon = getListRowIcon(group)
                const priceCents = priceCentsByGroup[group]
                const isSelected = selected === group
                // Absent from priceCentsByGroup means no nearby location — not the same
                // as a present null price ("Priced after visit" with locations). Disabled
                // while loading too: priceCentsByGroup is still empty then, and selecting
                // would park the drawer on an empty filtered view once results arrive.
                const hasNearbyLocation = group in priceCentsByGroup
                const isDisabled = isLoading || !hasNearbyLocation

                return (
                    <UnstyledButton
                        key={group}
                        onClick={() => onSelect(isSelected ? null : group)}
                        disabled={isDisabled}
                        className={classes.chip}
                        data-selected={isSelected}
                    >
                        <Stack gap={9} align="center">
                            {icon && (
                                icon.isWordmark ? (
                                    <Image src={icon.src} alt="" width={40} height={22} className={classes.wordmark} />
                                ) : (
                                    <Image src={icon.src} alt="" width={24} height={24} />
                                )
                            )}
                            <Stack gap={4} align="center">
                                {isLoading ? (
                                    <Skeleton height={24} width={64} />
                                ) : (
                                    <Text fz={16} fw="var(--gm-weight-text-bold, 600)" c="var(--gm-foreground-fg-primary)">
                                        {priceCents != null ? humanizePrice(priceCents) : 'Priced after visit'}
                                    </Text>
                                )}
                                <Text fz={14} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-secondary)">
                                    {LABEL_BY_PRICING_GROUP[group]}
                                </Text>
                            </Stack>
                        </Stack>
                    </UnstyledButton>
                )
            })}
        </Group>
    )
}
